import { BUILT_GROUPS } from "@/lib/built-catalogue";
import { CASE_STUDIES } from "@/lib/case-studies";
import { SERVICES } from "@/lib/services";

/**
 * THE MARKETS WE ARE DELIBERATELY GOING AFTER, AND WHAT WE CAN HONESTLY SAY ABOUT EACH.
 *
 * ────────────────────────────────────────────────────────────────────────────────
 *  WHEN AN INDUSTRY PAGE IS ALLOWED TO EXIST. It used to be one rule, written in
 *  `sectors.ts`: at least one live, named, checkable build in that sector. That
 *  rule was protecting against a real failure, a page with nothing behind it that
 *  has to be vague, and vague is worse than absent. It is not the rule any more,
 *  because it also blocked a market we have decided to go after and can genuinely
 *  build for.
 *
 *  A page may exist if EITHER
 *    A. there is genuine delivered proof in that sector, OR
 *    B. it is a market we are deliberately targeting, we have the technical
 *       capability to build useful systems for it, and there is enough specific
 *       knowledge of how those businesses run to make a page worth reading.
 *
 *  What did NOT change, and must not: proof is still proof. A delivered-work
 *  section is generated from real case studies and real catalogue entries, and
 *  disappears completely when there are none. Nothing borrows a project from
 *  another sector to look experienced. B buys the right to describe capability,
 *  never the right to imply delivery.
 * ────────────────────────────────────────────────────────────────────────────────
 *
 * TWO INDUSTRIES DELIBERATELY HAVE NO PAGE HERE. `/coaches` and `/accountants`
 * already exist, are the strongest pages we have in their markets, and carry
 * whatever equity they have earned. Consistency of route shape is not worth an
 * avoidable redirect, so they are listed with their real `href`, the hub links
 * straight to them, and the generator skips them.
 *
 * ACCOUNTANCY SITS UNDER PROFESSIONAL SERVICES RATHER THAN BESIDE IT, for the
 * same reason: two pages competing for one query is the thing this architecture
 * exists to avoid. Professional services is the broader market and says so, and
 * it points at `/accountants` as the narrower page where we have actually
 * delivered. Their search intent is different and so is their content.
 */

export type IndustryPage = {
  heading: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  /** How these businesses actually run. Sector specific, or the page has no reason to exist. */
  howTheyRun: readonly string[];
  /** Where the work goes wrong in this sector. Symptoms, never a claim about this reader. */
  whereItHurts: readonly string[];
  /** CAPABILITY. Things we can build for this market. Never a claim of delivery. */
  canBuild: readonly string[];
  /** How a project in this market would run. */
  approach: readonly string[];
  /** EVIDENCE. Catalogue ids, validated. Empty means the proof section does not render. */
  builtIds: readonly string[];
  /** EVIDENCE. Case study slugs, validated. Empty means the work section does not render. */
  caseStudySlugs: readonly string[];
  /** Services worth reading next, by service slug. Validated. */
  relatedServiceSlugs: readonly string[];
  /** A narrower page inside this market, where one exists and has earned it. */
  moreSpecific?: { name: string; href: `/${string}`; note: string };
  /**
   * EVIDENCE YOU CAN SEE. Real screenshots of one of our own demonstrations, taken from the
   * live deployment, never mocked up. The company in it must be INVENTED (see demo-films.ts):
   * a demo built for a real named firm never goes on this site.
   */
  showcase?: IndustryShowcase;
  /**
   * Questions a buyer in this market actually asks, answered from what the demonstration or the
   * delivered work really does. Rendered on the page AND as FAQPage data, so never add one that is
   * not visible, and never answer with a feature that has no source behind it.
   */
  faq?: readonly { question: string; answer: string }[];
  /** The closing card leads with booking a review rather than the briefing. Search pages only. */
  bookFirst?: boolean;
};

export type IndustryShowcase = {
  heading: string;
  body: string;
  /** The invented company, named so the caption can say it is invented. */
  demoName: string;
  demoHref: `https://${string}`;
  desktop: { src: `/industries/${string}`; alt: string; width: number; height: number };
  phone: { src: `/industries/${string}`; alt: string; width: number; height: number };
};

export type Industry = {
  slug: string;
  href: `/${string}`;
  name: string;
  /** One line on the hub. */
  hubBlurb: string;
  /**
   * True when the page rests on delivered work in this sector, false when it rests on
   * capability. Read by the hub so it can be honest about which is which without anybody
   * having to remember to write it, and by the page to decide whether proof sections exist.
   */
  hasDeliveredWork: boolean;
  /** Absent for industries whose page already lives somewhere else. */
  page?: IndustryPage;
};

export const INDUSTRIES: readonly Industry[] = [
  {
    slug: "construction-and-trades",
    href: "/industries/construction-and-trades",
    name: "Construction and trades",
    hubBlurb:
      "Quoting, job tracking and customer updates for businesses whose work happens on a site rather than at a desk.",
    hasDeliveredWork: true,
    page: {
      heading: "Software for construction and trades businesses",
      metaTitle: "Software for construction and trades businesses | Ownly Digital",
      metaDescription:
        "Custom software for construction firms and trades businesses: estimating and quoting, job management, scheduling, customer portals and field friendly tools, built around how the work actually runs.",
      intro:
        "A building job is a chain of handovers, and most of what goes wrong happens in the gaps between them. The quote that took a week, the variation nobody wrote down, the customer who has not been told anything for a fortnight.",
      howTheyRun: [
        "Work arrives as enquiries that need pricing before anybody knows whether they are worth having, and the pricing depends on measurements, materials and who is available.",
        "A job then runs across people who are rarely in the same place: an estimator, whoever is on site, a supplier, a client who wants to know when. The office is where the information is supposed to meet, and usually where it stops.",
        "Money follows stages rather than a single invoice, so deposits, variations and retentions all have to be tracked against a job that is still moving.",
      ],
      whereItHurts: [
        "Quotes take days because the numbers live in somebody's head and a spreadsheet",
        "Two estimators price the same job differently and both can defend it",
        "A variation is agreed on site, remembered by one person, and invoiced by nobody",
        "The customer rings for an update and somebody has to ring the site to find out",
        "Photographs of finished work sit on phones instead of against the job",
        "The diary is a whiteboard, and the whiteboard is not in the van",
      ],
      canBuild: [
        "Estimating that produces the same number twice, from rules you control",
        "Quotes out the same day, with follow up that happens whether or not anybody remembers",
        "A job record that holds the quote, the variations, the photographs and the paperwork",
        "Scheduling that shows who is where, and survives being looked at on a phone",
        "A customer portal with the job status and the next date, so the update call stops",
        "Site friendly capture: photographs and notes that upload when the signal comes back",
        "Deposits, stage payments and reminders, tied to the job rather than to an inbox",
        "Certificates, sign offs and documents stored against the job, not emailed around",
        "A dashboard that says what is live, what is late and what is unpriced",
      ],
      approach: [
        "We start with one job, followed end to end as it really happens, including the parts that only work because somebody rings somebody. Construction software usually fails because it was designed for the process on paper rather than the one in the van.",
        "Then the narrowest useful piece goes live: usually quoting, because it is the part that costs the most time and is the easiest to measure afterwards.",
      ],
      builtIds: [],
      caseStudySlugs: ["meridian-supply-co"],
      relatedServiceSlugs: [
        "quoting-and-estimating",
        "internal-business-systems",
        "client-portals",
      ],
      moreSpecific: {
        name: "Property maintenance companies",
        href: "/industries/property-maintenance",
        note: "The narrower page, for firms doing repairs and maintenance for letting agents, housing providers and landlords.",
      },
    },
  },
  {
    slug: "professional-services",
    href: "/industries/professional-services",
    name: "Professional services",
    hubBlurb:
      "Client onboarding, portals, document workflows and the recurring work that fills a practice's week.",
    hasDeliveredWork: true,
    page: {
      heading: "Software for professional services firms",
      metaTitle: "Software for professional services firms | Ownly Digital",
      metaDescription:
        "Custom software for professional services: client onboarding, secure portals, document and approval workflows, recurring work tracking and reporting, built around how a practice actually delivers.",
      intro:
        "A practice sells time and judgement, and then spends a surprising amount of both on admin that nobody is paying for. Onboarding, chasing documents, telling clients where things are.",
      howTheyRun: [
        "Work is either a matter with a start and an end, or a recurring obligation with a deadline that arrives whether or not anybody prepared for it. Most firms run both at once.",
        "Almost every step needs something from the client, which means the firm's timeline is really the client's timeline, and chasing is the hidden job underneath all of it.",
        "Confidentiality is not a preference. Documents have to move without ending up in a forwarded email chain, and who saw what has to be answerable later.",
      ],
      whereItHurts: [
        "Onboarding a client is a chain of emails that stalls whenever one person is away",
        "The same document is requested twice because nobody can see it already arrived",
        "Deadlines are tracked in a spreadsheet that one person maintains",
        "Client questions about progress are answered by a person, one at a time",
        "Sensitive files are sent as attachments because that is the only easy route",
        "Nobody can say which recurring jobs are at risk until the week they are due",
      ],
      canBuild: [
        "Client onboarding that runs itself, with the chasing built in",
        "A secure portal where a client sees their own matter, documents and next step",
        "Document requests and uploads without the client creating an account",
        "E signature on engagement letters, filed against the client automatically",
        "Recurring work tracked by deadline, with early warning rather than a late surprise",
        "Approvals with a record of who agreed to what and when",
        "Reporting on capacity, work in progress and what is actually at risk",
        "Permissions, so staff see their clients and partners see the practice",
      ],
      approach: [
        "The first question is which part of the process the client is actually holding up, because that is usually where the time goes and it is rarely where firms look first.",
        "Everything is built to be defensible afterwards. In a practice that advises on compliance, an audit trail is not a feature request, it is the reason the system can be trusted at all.",
      ],
      builtIds: [
        "client-portal",
        "client-intake-forms",
        "token-link-documents",
        "documents-behind-a-gate",
        "esign-retainer",
        "automatic-chasing",
        "ie-audit-trail",
        "mta-one-next-step-per-page",
      ],
      caseStudySlugs: ["my-trusted-accountant"],
      relatedServiceSlugs: [
        "client-portals",
        "internal-business-systems",
        "business-process-automation",
      ],
      moreSpecific: {
        name: "Accountancy practices",
        href: "/accountants",
        note: "The narrower page, and the one with the delivered work behind it.",
      },
    },
  },
  {
    slug: "recruitment",
    href: "/industries/recruitment",
    name: "Recruitment",
    hubBlurb:
      "Candidate and client workflows, vacancy management and the admin that sits between a placement and getting paid.",
    hasDeliveredWork: false,
    page: {
      heading: "Software for recruitment businesses",
      metaTitle: "Software for recruitment agencies and businesses | Ownly Digital",
      metaDescription:
        "Custom software for recruitment businesses: candidate and client workflows, vacancy management, compliance documents, placement tracking and the automation that removes repeated admin.",
      intro:
        "Recruitment runs on two pipelines at once, and the money is made in the overlap. Most of the working week goes on keeping both up to date rather than on the conversations that actually place people.",
      howTheyRun: [
        "A consultant is managing candidates and clients at the same time, and the same person is the interface to both. The system has to hold two relationships against one vacancy.",
        "Speed decides outcomes. The first credible CV in front of a hiring manager wins more often than the best one sent on Friday, so anything that slows a submission costs a placement.",
        "Compliance and paperwork sit between a placement and an invoice: right to work, references, contracts, timesheets. None of it is the job, all of it stops the money.",
      ],
      whereItHurts: [
        "The same candidate exists three times because three consultants added them",
        "A vacancy is filled and the other candidates hear nothing, so they do not come back",
        "Notes live in an inbox, so a handover loses half the context",
        "Compliance documents are chased by hand and found missing at the worst moment",
        "Nobody can see today's actual priority without reading three lists",
        "Placements are tracked well enough to bill, and not well enough to learn from",
      ],
      canBuild: [
        "One record per candidate and per client, with duplicates merged rather than collected",
        "Vacancy management that shows the pipeline per role, not just per consultant",
        "Submissions that take minutes, using what is already on the record",
        "Automatic follow up for candidates who did not get it, so the pool stays warm",
        "Compliance checklists per placement, blocking the step that cannot be skipped",
        "Document collection by link, without a candidate creating an account",
        "Dashboards by consultant, client and role, built from the work rather than from a report",
        "Integrations into the job boards, calendars and accounts systems already in use",
      ],
      approach: [
        "We would start where the delay actually is, which in most agencies is between a candidate saying yes and a client seeing them, rather than in sourcing.",
        "A recruitment system lives or dies on whether consultants update it, so the first build has to save the consultant time on day one. Anything that only benefits management gets filled in badly and then trusted anyway, which is worse than not having it.",
      ],
      builtIds: [],
      caseStudySlugs: [],
      relatedServiceSlugs: [
        "crm-development",
        "business-process-automation",
        "internal-business-systems",
      ],
    },
  },
  {
    slug: "fitness-and-coaching",
    /* The existing page. Stronger than anything written from scratch and already ranked. */
    href: "/coaches",
    name: "Fitness and coaching",
    hubBlurb:
      "Programmes, check-ins and client apps for coaches, with real coaching businesses running on them and apps live on both stores.",
    hasDeliveredWork: true,
  },
  {
    slug: "events-and-entertainment",
    href: "/industries/events-and-entertainment",
    name: "Events and entertainment",
    hubBlurb:
      "Enquiries, bookings, ticketing and the night itself, for businesses that sell an occasion rather than an hour.",
    hasDeliveredWork: true,
    page: {
      heading: "Software for events and entertainment businesses",
      metaTitle: "Software for events and entertainment businesses | Ownly Digital",
      metaDescription:
        "Custom software for events and entertainment: enquiry and booking workflows, ticketing and door scanning, artist and supplier management, deposits and automated client communication.",
      intro:
        "Events are sold on feel and delivered on logistics. The website has to make somebody want the night, and everything behind it has to make sure the night actually happens.",
      howTheyRun: [
        "An enquiry is a date, a place and a feeling, and it competes with two or three other suppliers who were asked the same week. Whoever answers properly first is usually the one who gets it.",
        "Between the booking and the event there is a long quiet period full of detail: times, set lists, access, the couple changing their minds, a supplier who needs confirming.",
        "The day itself is unforgiving. Anything that depends on one person having the right information on their phone is a risk, and nothing can be fixed after the fact.",
      ],
      whereItHurts: [
        "Enquiries arrive without the details needed to answer them, so the first reply is a question",
        "Availability is checked by ringing somebody, and double bookings have happened",
        "Deposits are chased manually and some bookings are held on nothing",
        "The run sheet lives in a document that three people have different versions of",
        "Tickets are sold through a platform that takes a cut and owns the customer",
        "After the event, nobody asks for the review while the night is still fresh",
      ],
      canBuild: [
        "An enquiry form that asks what a planner would ask and files itself into your system",
        "Availability and booking with deposits taken at the point of agreement",
        "Ticketing on your own site, with payouts, transfers and wallet passes",
        "Scanning on the door, handed to somebody who is not you",
        "Artist, act and supplier profiles with video, editable by your own team",
        "Automatic client communication in the quiet period, so nobody is chasing you",
        "Run sheets and event detail in one place, readable on a phone at a venue",
        "Post event review collection, sent while the night is still fresh",
        "A page for every area you cover, so search finds you where you actually work",
      ],
      approach: [
        "The public side and the operational side get built as one thing. An events site that wins the enquiry and then hands it to a spreadsheet has moved the bottleneck rather than removed it.",
        "We hand over the keys. Acts change, venues change and packages change, so a site the business cannot edit is out of date within a month.",
      ],
      builtIds: [
        "multi-step-booking-sync",
        "artist-video-profiles",
        "gs-ticket-payouts",
        "gs-door-scan",
        "gs-ticket-wallet",
        "gs-ticket-transfer",
        "gs-event-analytics",
        "gs-calendar-sync",
        "a-page-for-every-place-you-work",
        "review-collection",
        "self-serve-cms",
      ],
      caseStudySlugs: ["the-dfc-experience"],
      relatedServiceSlugs: [
        "booking-systems",
        "website-design-and-development",
        "internal-business-systems",
      ],
      moreSpecific: {
        name: "Entertainment agencies and wedding entertainers",
        href: "/industries/entertainment-agencies",
        note: "The narrower page, for agencies and acts booking DJs, hosts and live performers.",
      },
    },
  },
  /*
   * GYMS, PROPERTY MAINTENANCE AND ENTERTAINMENT AGENCIES, added 2026-10-08 as search pages.
   *
   * Picked from Search Console, not guessed: the narrow "software for <trade>" pages already on
   * this site (lettings, aesthetics) reached positions 2 to 5 within weeks, while the broad
   * "custom business software" page sits around 79. Each of the three also has one of our own
   * demonstrations behind it, so the page can SHOW the software rather than describe it.
   *
   * Every demonstration named here is an invented company. Gyms and property maintenance rest on
   * rule B (no delivered work in the sector, so no proof is cited). Entertainment agencies rest on
   * rule A: The DFC Experience is real client work in exactly this market.
   */
  {
    slug: "gyms-and-leisure",
    href: "/industries/gyms-and-leisure",
    name: "Gyms and leisure",
    hubBlurb:
      "Machine faults, contractor repairs and daily shift checks for gym owners who are tired of the clipboard and the group chat.",
    hasDeliveredWork: false,
    page: {
      heading: "Software for gyms and leisure operators",
      metaTitle: "Gym maintenance and operations software | Ownly Digital",
      metaDescription:
        "Custom software for gym owners: equipment fault reporting, contractor work orders and quotes, sign off before a machine goes back on the floor, and shift checklists with a proper handover.",
      intro:
        "A cable frays on a Tuesday. Somebody tapes a sign to the machine, somebody else posts in the group chat, and by Friday nobody can say whether the engineer came, what he did, or who decided it was safe to use again.",
      showcase: {
        heading: "What it looks like running",
        body: "Ironmere is a demonstration we built for an invented gym group with three clubs. The owner's screen shows how many machines are on the floor, the days lost to repairs, the quotes waiting on a decision and the checks that were missed. The phone screen is what a manager carries round the club, and it keeps working in a room with no signal.",
        demoName: "Ironmere",
        demoHref: "https://ironmere-demo.vercel.app",
        desktop: {
          src: "/industries/gyms-ironmere-overview.jpg",
          alt: "Ironmere owner overview across three clubs: 94.4 percent of machines available, 80.2 days lost in 90 days, quotes to decide, and a list of repairs and missed checks that need attention",
          width: 1800,
          height: 1050,
        },
        phone: {
          src: "/industries/gyms-ironmere-phone.jpg",
          alt: "Ironmere phone screen for a club manager, with buttons to scan or find a machine and a note that it is ready to work with no signal",
          width: 780,
          height: 1688,
        },
      },
      howTheyRun: [
        "A gym sells access to its kit. Every machine that is out of use is something members are paying for and cannot have, and they notice before the owner does.",
        "Faults are spotted by whoever is on the floor: a coach, a cleaner, a member at the desk. Repairs are done by contractors who visit one club this week and another next. The person who finds the problem and the person who fixes it rarely meet.",
        "Underneath that sit the daily checks for opening, cleaning, safety and closing. Staff on rotating shifts do them on paper, and a paper sheet can be ticked all at once at the end of the day.",
      ],
      whereItHurts: [
        "A broken machine is reported three different ways and written down in none of them",
        "Nobody knows how long a machine has been out of use, so nobody knows what it is costing",
        "A contractor's quote sits in an inbox until somebody remembers to approve it",
        "The engineer says it is fixed and it goes straight back on the floor with nobody checking",
        "The cleaning sheet is fully ticked at half past five, including the jobs nobody did",
        "The plant room in the basement has no signal, so the app you tried down there was useless",
      ],
      canBuild: [
        "Fault reports from the gym floor, made by scanning a code on the machine",
        "Work orders that follow each fault to a contractor, with the quote attached",
        "Spending limits, so a quote over a set figure waits for the right person",
        "A sign off step: a repaired machine stays out of use until a named manager puts it back",
        "Days out of use counted per machine and per club, from the records rather than a guess",
        "Shift checklists split by the hour, so nothing can be ticked late or early",
        "A handover at the end of each shift with both names on it and anything missed carried over",
        "Phone screens that work with no signal and catch up when it returns",
        "One view across every club for the owner",
      ],
      approach: [
        "We would start with one machine, followed from the moment somebody notices it is broken to the moment it is back in use. That path runs through a coach, a manager, a contractor and a manager again, and it is where the money goes.",
        "Staff will only use something quicker than a sign and some tape, so the phone side is built first and tested in the worst room in the building.",
      ],
      builtIds: [],
      caseStudySlugs: [],
      relatedServiceSlugs: [
        "internal-business-systems",
        "business-process-automation",
        "quoting-and-estimating",
      ],
      faq: [
        {
          question: "Can staff report a broken machine without sitting at a computer?",
          answer:
            "Yes. Each machine gets a printed code. Scanning it with a phone camera opens that machine's record, and the fault is reported from there with a photo. In our Ironmere demonstration the phone's own camera app opens the right record.",
        },
        {
          question: "Will it work in a basement with no phone signal?",
          answer:
            "It can be built to. The phone keeps the machines and checks it needs, carries on without a connection, and sends what was recorded once the signal returns. Our Ironmere demonstration is tested with the network switched off.",
        },
        {
          question: "Can it cover daily cleaning and safety checks as well as repairs?",
          answer:
            "Yes. Checks are grouped by the hour they belong to, signed with a name, and handed over at the end of the shift with anything missed carried forward. Our Shift demonstration shows this for gyms, salons, nurseries and care homes.",
        },
        {
          question: "We already have membership software. Does this replace it?",
          answer:
            "No. Membership software looks after members and payments. This is the operations side: the kit, the contractors and the checks. It is built to run beside what you already use for members.",
        },
      ],
      bookFirst: true,
    },
  },
  {
    slug: "property-maintenance",
    href: "/industries/property-maintenance",
    name: "Property maintenance",
    hubBlurb:
      "Job management for maintenance firms working for letting agents, housing providers and landlords, where the extra work found on site is where the margin goes.",
    hasDeliveredWork: false,
    page: {
      heading: "Software for property maintenance companies",
      metaTitle: "Job management software for property maintenance firms | Ownly Digital",
      metaDescription:
        "Custom job management software for property maintenance and repairs firms: jobs from agents and landlords, extra work priced and approved on site, photos against the job, and invoices that match what was agreed.",
      intro:
        "A joiner lifts a fire door off and the frame comes away with it. Somebody at the customer says crack on. Nobody writes it down, and at invoice time the extra work is either argued over or quietly dropped.",
      showcase: {
        heading: "What it looks like running",
        body: "Brackenhall is a demonstration we built for an invented joinery and property maintenance firm. The office screen shows what is waiting on a price, what is sitting with customers and which job cannot be invoiced yet. The phone screen is the joiner's day, and it flags the job that is still missing its after photos.",
        demoName: "Brackenhall",
        demoHref: "https://ownly-brackenhall.vercel.app",
        desktop: {
          src: "/industries/property-maintenance-brackenhall-office.jpg",
          alt: "Brackenhall office overview: extra work approved this month, extra work waiting on a price and sitting with customers, jobs waiting to be given out and operatives on site",
          width: 1800,
          height: 1038,
        },
        phone: {
          src: "/industries/property-maintenance-brackenhall-phone.jpg",
          alt: "Brackenhall phone screen for a joiner, showing an open job with a warning that the after photos have not been added",
          width: 780,
          height: 1560,
        },
      },
      howTheyRun: [
        "Most of the work comes from a handful of customers: letting agents, housing providers, commercial landlords. Each sends a steady stream of repairs, and each has its own way of approving them.",
        "A job is booked by the office, done by somebody in a van and approved by somebody at the customer who was not there. Every one of those handovers is a chance for a detail to go missing.",
        "Finding extra work on site is normal. Getting it priced and agreed before anyone starts is not, and the gap between the two is where the money leaks out.",
      ],
      whereItHurts: [
        "Jobs arrive by email, phone and text, and the office types each one in again",
        "Extra work is agreed by a text on site and nobody can find it at invoice time",
        "A job cannot be invoiced because the after photos are still on someone's phone",
        "The office rings the van to find out where a job is up to",
        "Every customer approves work differently and the rules live in one person's head",
        "Nobody can say how much priced extra work is sitting with customers unanswered",
      ],
      canBuild: [
        "One record per job holding the booking, the photos, the notes and every approval in order",
        "Extra work priced on site and sent to the customer to approve from a link",
        "A rule that holds a job back from invoicing while anything on it is unapproved or missing",
        "A phone screen for each operative with today's jobs, working with no signal",
        "Before and after photos taken against the job, not left in a camera roll",
        "A board showing who is out, what needs a price and what is sitting with customers",
        "Approval rules and paperwork per customer, so they live in the system",
        "A view for each customer of their own jobs, so the status call stops",
        "Invoices built from the job record, so the total matches what was agreed",
      ],
      approach: [
        "We would start with one job that went wrong: extra work that was never billed, or an invoice that came back disputed. Followed from the first call to the last payment, it usually shows exactly where the record breaks.",
        "Operatives get the phone screen first, and it has to ask them for less than they do now. If it is slower than texting the office, it will not get used.",
      ],
      builtIds: [],
      caseStudySlugs: [],
      relatedServiceSlugs: [
        "quoting-and-estimating",
        "internal-business-systems",
        "client-portals",
      ],
      faq: [
        {
          question: "Can customers approve extra work without downloading an app?",
          answer:
            "Yes. The price goes to them as a link. They open it on their phone, see the work and the photograph, and approve or decline. The answer is saved against the job with their name and the time, so it is there when the invoice goes out.",
        },
        {
          question: "What happens when an operative has no signal on site?",
          answer:
            "The phone keeps the day's jobs and lets them carry on. Notes and photographs are held on the phone and sent to the office as soon as the signal comes back.",
        },
        {
          question: "Can it pass finished jobs to our accounts package?",
          answer:
            "It can be built to, so nothing is typed twice. Which package you use is one of the first things we ask, because it decides how invoices leave the system.",
        },
      ],
      bookFirst: true,
    },
  },
  {
    slug: "entertainment-agencies",
    href: "/industries/entertainment-agencies",
    name: "Entertainment agencies and wedding entertainers",
    hubBlurb:
      "Bookings, client changes and performer briefs for agencies and acts, so the DJ on the night has the same plan as the couple.",
    hasDeliveredWork: true,
    page: {
      heading: "Software for entertainment agencies and wedding entertainers",
      metaTitle: "Booking software for entertainment agencies and wedding DJs | Ownly Digital",
      metaDescription:
        "Custom booking software for entertainment agencies, DJs and wedding entertainers: enquiries into the diary, client changes priced before they are added, numbered performer briefs and a couple's own planning page.",
      intro:
        "Three weeks before the wedding the couple move the first dance. The office knows. The DJ is still working from the running order he was sent a month ago, and finds out at the venue.",
      showcase: {
        heading: "What it looks like running",
        body: "Juniper is a demonstration we built for an invented wedding and events entertainment supplier. The office screen holds the diary, the clashes, the client requests waiting on a price and the briefs nobody has opened. The phone screen is the DJ's, with one brief per event and a version number on each.",
        demoName: "Juniper Events",
        demoHref: "https://juniper-events-one.vercel.app",
        desktop: {
          src: "/industries/entertainment-juniper-office.jpg",
          alt: "Juniper Events office screen: the next fortnight's bookings, a performer clash, a client request to review, briefs not yet acknowledged and the diary with fees for each event",
          width: 1800,
          height: 1025,
        },
        phone: {
          src: "/industries/entertainment-juniper-performer.jpg",
          alt: "Juniper Events performer screen for a DJ, showing two upcoming weddings, one brief acknowledged at version 3 and one where version 2 is new",
          width: 780,
          height: 1200,
        },
      },
      howTheyRun: [
        "An agency sells acts: DJs, hosts, bands, saxophonists. Each booking pairs a client with one or more performers, often months before the night.",
        "Between the booking and the event the details change. Times move, extras get added, the venue changes its rules. Each change has to reach the office, the client and the performer, and match in all three places.",
        "On the night the performer is on their own, often somewhere with poor signal, working from whatever they were last sent.",
      ],
      whereItHurts: [
        "Enquiries arrive without a date, a venue or a budget, so the first reply is three questions",
        "A couple asks for an extra hour on WhatsApp and nobody prices it",
        "The performer turns up with last month's running order",
        "Two acts are pencilled in for the same Saturday and nobody spots it until the week before",
        "Deposits and balances live in a spreadsheet one person understands",
        "Nobody knows whether the performer actually read the final brief",
      ],
      canBuild: [
        "An enquiry form that asks what your office would ask and files the booking into your diary",
        "A diary showing every act, every date and every clash across the agency",
        "Client requests priced and accepted before anything changes in the plan",
        "A planning page for each couple, with their answers going straight into the brief",
        "Numbered briefs, so everyone knows which version they are working from",
        "Performers confirming they have read the latest version, with the office able to see who has not",
        "A performer screen that still opens with no signal at the venue",
        "Deposits, balances and what is left to collect against each booking",
        "Artist pages with video, edited by your own team",
      ],
      approach: [
        "We would start with one booking that changed after it was confirmed, followed from the request to the night. It shows exactly where the versions split.",
        "The client's page and the performer's screen are built alongside the office, not after it. A brief that nobody reads on the night is not finished.",
      ],
      builtIds: [
        "multi-step-booking-sync",
        "artist-video-profiles",
        "review-collection",
        "self-serve-cms",
      ],
      caseStudySlugs: ["the-dfc-experience"],
      relatedServiceSlugs: [
        "booking-systems",
        "client-portals",
        "internal-business-systems",
      ],
      faq: [
        {
          question: "Can couples see their booking without ringing the office?",
          answer:
            "Yes. Each couple gets their own page with their planning form, their running order and a way to ask for a change. A change they ask for is priced and accepted before it goes into the plan.",
        },
        {
          question: "How do I know a performer has read the latest brief?",
          answer:
            "Every brief has a version number. When a new version is published the performer is asked to confirm it, and the office can see who has confirmed and who has not.",
        },
        {
          question: "Have you built for an entertainment business before?",
          answer:
            "Yes. We built the booking site for The DFC Experience, a wedding and event entertainment company: artist pages with video, a booking form that asks what a planner would ask, and a studio their own team uses to edit the site.",
        },
      ],
      bookFirst: true,
    },
  },
  /*
   * ESTATE AGENTS AND CLINICS, added 2026-09-19 because both are call campaign industries in the
   * September marketing plan, and its rule is that a campaign's page is live before the campaign
   * starts. Both rest on rule B (targeted market, real capability, no delivered work), so no proof
   * is cited. Never claim a portal integration (Rightmove, Zoopla) or a clinical compliance
   * standard here: neither has been built, and both are things a buyer would check.
   */
  {
    slug: "estate-and-letting-agents",
    href: "/industries/estate-and-letting-agents",
    name: "Estate and letting agents",
    hubBlurb:
      "Enquiries answered while the office is shut, viewings booked without the phone tag, and landlords who can see what is happening.",
    hasDeliveredWork: false,
    page: {
      heading: "Software for estate and letting agents",
      metaTitle: "Software for estate and letting agents | Ownly Digital",
      metaDescription:
        "Custom software for estate and letting agents: instant replies to enquiries out of hours, viewing booking, vendor and landlord portals, compliance reminders and tenancy workflows, built around how a branch actually runs.",
      intro:
        "Most property enquiries arrive when the branch is closed, from people who have asked three other agents about the same house. The agent who replies properly first usually gets the viewing.",
      howTheyRun: [
        "Enquiries come in from the portals, the website and the phone, mostly in the evening and at the weekend, and each one has to become a viewing before the buyer or tenant loses interest.",
        "A sale or a let is a long chain of people who all want updates: vendors, buyers, landlords, tenants, solicitors, contractors. Much of the week goes on telling them where things are.",
        "Lettings adds a calendar that never stops: renewals, inspections, safety certificates and deposits, each with a date that matters whether or not anybody remembered it.",
      ],
      whereItHurts: [
        "An enquiry lands at eight in the evening and gets its first reply the next morning",
        "Arranging a viewing takes four messages and a phone call",
        "Vendors ring to ask how the viewings went because nobody has told them",
        "Viewing feedback is written on paper and typed up later, if at all",
        "Certificate and renewal dates live in a spreadsheet one person keeps",
        "Maintenance requests arrive by text, email and phone, and some get lost between them",
      ],
      canBuild: [
        "An instant reply to every enquiry, day or night, with real viewing slots attached",
        "Viewing booking that checks the diary and confirms without anybody picking up the phone",
        "A vendor portal showing viewings, feedback and offers as they happen",
        "A landlord portal with rent, maintenance, certificates and documents in one place",
        "Reminders for safety certificates, inspections and renewals, well before they are due",
        "Maintenance requests logged once, with photos, and passed to the right contractor",
        "Feedback captured on a phone straight after the viewing",
        "A dashboard of what is under offer, what is stuck and what needs chasing",
      ],
      approach: [
        "We would start with the evening enquiry, followed from the moment it arrives to the moment a viewing is booked. It is where most agents lose business, and the easiest thing to measure afterwards.",
        "The system works alongside the software a branch already uses rather than replacing it on day one. Agents change systems rarely and reluctantly, and a tool that asks them to re-key everything does not get used.",
      ],
      builtIds: [],
      caseStudySlugs: [],
      relatedServiceSlugs: [
        "crm-development",
        "client-portals",
        "business-process-automation",
      ],
    },
  },
  {
    slug: "clinics-and-aesthetics",
    href: "/industries/clinics-and-aesthetics",
    name: "Clinics and aesthetics",
    hubBlurb:
      "Consultations, bookings, aftercare and reviews for clinics whose best clients are happy and never think to say so.",
    hasDeliveredWork: false,
    page: {
      heading: "Software for clinics and aesthetics practices",
      metaTitle: "Software for clinics and aesthetics practices | Ownly Digital",
      metaDescription:
        "Custom software for clinics and aesthetics practices: online booking and deposits, consultation and consent forms, aftercare follow up and review requests sent at the right moment, built around how a clinic actually runs.",
      intro:
        "People choose a clinic on trust, and they judge trust mostly on what other clients say. The clients who would say the best things are usually the ones who leave happy and never think to write it down.",
      howTheyRun: [
        "A treatment starts before the appointment: an enquiry, a consultation, forms, sometimes a deposit. Each step is a chance for somebody to go quiet and book elsewhere.",
        "After the treatment comes aftercare and, for many treatments, a return visit weeks or months later. That rebooking is where a lot of a clinic's income comes from, and it depends on somebody remembering to ask.",
        "Client information is sensitive, so forms, notes and photographs have to be kept properly and seen only by the people who need them.",
      ],
      whereItHurts: [
        "Happy clients leave without being asked for a review, and unhappy ones find the page themselves",
        "Consultation forms are filled in on paper in the waiting room",
        "No shows cost a slot, and nobody was reminded the day before",
        "Deposits are taken by phone, or not at all",
        "The rebooking date for a treatment lives in somebody's memory",
        "Before and after photos sit on a personal phone",
      ],
      canBuild: [
        "Review requests sent after a good appointment, with a direct link to the right review page",
        "Online booking with deposits taken when the slot is chosen",
        "Consultation and consent forms completed before the client arrives",
        "Reminders the day before, so fewer slots are lost to no shows",
        "Aftercare messages and a prompt to rebook when the treatment is due again",
        "Client records with notes and photos, visible only to the right staff",
        "A treatment menu with prices and what to expect, written for people who are nervous",
        "A dashboard showing bookings, no shows, rebookings and new reviews",
      ],
      approach: [
        "We would start with what happens after the appointment, because that is where reviews and rebookings are won or lost, and it is usually the part nobody owns.",
        "Everything is built so client information stays with the people who need it. Who can see what is decided at the start rather than added later.",
      ],
      builtIds: [],
      caseStudySlugs: [],
      relatedServiceSlugs: [
        "booking-systems",
        "business-process-automation",
        "website-design-and-development",
      ],
    },
  },
  {
    slug: "accountancy",
    /* Narrower than professional services, older than this registry, and the one with the
       delivered work. Listed so it stays discoverable rather than being quietly demoted. */
    href: "/accountants",
    name: "Accountancy practices",
    hubBlurb:
      "The narrower professional services page, built around a practice competing on credibility rather than price.",
    hasDeliveredWork: true,
  },
];

/**
 * EVERY REFERENCE CHECKED AT IMPORT. Same reasoning as `services.ts`: a proof link is a
 * promise that a real thing sits behind it, and these pages are read by people deciding
 * whether to believe us. A renamed catalogue id should stop the build, not quietly shorten
 * the evidence.
 */
const BUILT_IDS = new Set(
  BUILT_GROUPS.flatMap((group) => group.items.map((item) => item.id)),
);
const CASE_STUDY_SLUGS = new Set(CASE_STUDIES.map((study) => study.slug));
const SERVICE_SLUGS = new Set(SERVICES.map((service) => service.slug));

for (const industry of INDUSTRIES) {
  const where = `industries.ts: ${industry.slug}`;
  if (!industry.page) continue;
  for (const id of industry.page.builtIds) {
    if (!BUILT_IDS.has(id)) {
      throw new Error(`${where} cites built entry "${id}", which does not exist`);
    }
  }
  for (const slug of industry.page.caseStudySlugs) {
    if (!CASE_STUDY_SLUGS.has(slug)) {
      throw new Error(`${where} cites case study "${slug}", which does not exist`);
    }
  }
  for (const slug of industry.page.relatedServiceSlugs) {
    if (!SERVICE_SLUGS.has(slug)) {
      throw new Error(`${where} links to service "${slug}", which does not exist`);
    }
  }
  /*
   * The honesty invariant, enforced rather than trusted. `hasDeliveredWork` decides what the
   * hub is allowed to say about a page, so it cannot be allowed to drift away from whether
   * any delivered work is actually cited.
   */
  const cites =
    industry.page.builtIds.length > 0 || industry.page.caseStudySlugs.length > 0;
  if (cites !== industry.hasDeliveredWork) {
    throw new Error(
      `${where} says hasDeliveredWork=${industry.hasDeliveredWork} but ${
        cites ? "cites" : "cites no"
      } delivered work`,
    );
  }
}

/** The industries that get a generated page under `/industries/`. */
export const INDUSTRIES_WITH_PAGES = INDUSTRIES.filter(
  (industry): industry is Industry & { page: IndustryPage } => Boolean(industry.page),
);

export function getIndustry(slug: string): Industry | undefined {
  return INDUSTRIES.find((industry) => industry.slug === slug);
}

export function builtItemsForIndustry(page: IndustryPage) {
  const items = BUILT_GROUPS.flatMap((group) =>
    group.items.map((item) => ({ ...item, group })),
  );
  return page.builtIds
    .map((id) => items.find((item) => item.id === id))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));
}

export function caseStudiesForIndustry(page: IndustryPage) {
  return page.caseStudySlugs
    .map((slug) => CASE_STUDIES.find((study) => study.slug === slug))
    .filter((study): study is NonNullable<typeof study> => Boolean(study));
}

export function servicesForIndustry(page: IndustryPage) {
  return page.relatedServiceSlugs
    .map((slug) => SERVICES.find((service) => service.slug === slug))
    .filter((service): service is NonNullable<typeof service> => Boolean(service));
}
